import React, { useState } from "react";
import { Play, Video, Radio, ExternalLink, Calendar, Mail, Send, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NATION_SOCIALS = [
  {
    name: "Shaba Sharah - YouTube",
    url: "https://youtube.com/@shabasharah?si=94NEL8uvqAqjsCg4",
    description: "Music, teachings, and content from Shaba Sharah.",
    icon: "youtube",
  },
  {
    name: "The Woman In Gold - YouTube",
    url: "https://www.youtube.com/@TheWomanInGold77",
    description: "Scripture movies, feast day content, and prophetic insights.",
    icon: "youtube",
  },
  {
    name: "Shaba Sharah - TikTok",
    url: "https://www.tiktok.com/@shabasharah",
    description: "Short-form content and updates.",
    icon: "tiktok",
  },
  {
    name: "The Qadash Sheep - TikTok",
    url: "https://www.tiktok.com/@theqadashsheep77",
    description: "Daily teachings and prophetic clips.",
    icon: "tiktok",
  },
  {
    name: "Shaba Sharah - Spotify",
    url: "https://open.spotify.com/artist/nabyahshaba",
    description: "Scripture-based reggae and dancehall.",
    icon: "spotify",
  },
];

const RECENT_TEACHINGS = [
  {
    title: "The Exodus Pattern for Today",
    date: "January 2026",
    description: "Understanding how the ancient exodus applies to our current situation.",
  },
  {
    title: "Torah Living in Babylon",
    date: "December 2025",
    description: "Practical guidance for walking righteously while surrounded by lawlessness.",
  },
  {
    title: "Feast Days Explained",
    date: "November 2025",
    description: "Deep dive into the appointed times of YaHUaH.",
  },
];

export default function MediaHub() {
  const [bookingForm, setBookingForm] = useState({
    name: "",
    email: "",
    country: "",
    date: "",
    budget: "",
    details: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleBookingChange = (field, value) => {
    setBookingForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!bookingForm.name || !bookingForm.email || !bookingForm.country || !bookingForm.date) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...bookingForm,
        type: "shaba_booking",
      });
      setSubmitted(true);
      toast.success("Booking request submitted!");
    } catch (error) {
      console.error("Booking error:", error);
      toast.error("Failed to submit booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="media-hub-page">
      {/* Hero */}
      <section className="bg-royal-blue text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Radio className="w-8 h-8 text-kingdom-gold" />
            <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">
              Media Hub
            </span>
          </div>
          <h1 className="heading-hero mb-4">Truth on Every Frequency</h1>
          <p className="body-large text-slate-300 max-w-2xl">
            Connect with the nation across all platforms. Scripture teachings, feast day documentaries, 
            prophetic insights, and original music proclaiming the Name of YaHUaH/YaHUSHA.
          </p>
        </div>
      </section>

      {/* Nation/Gov Socials Block */}
      <section className="bg-parchment py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-section text-royal-blue mb-8">Official Channels</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {NATION_SOCIALS.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                data-testid={`social-link-${social.icon}`}
              >
                <Card className="card-official hover:shadow-lg transition-shadow h-full">
                  <CardHeader>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                      social.icon === "youtube" ? "bg-scarlett/10" :
                      social.icon === "tiktok" ? "bg-slate-900" :
                      "bg-green-100"
                    }`}>
                      {social.icon === "youtube" && <Video className="w-6 h-6 text-scarlett" />}
                      {social.icon === "tiktok" && (
                        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                        </svg>
                      )}
                      {social.icon === "spotify" && (
                        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-green-600">
                          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                        </svg>
                      )}
                    </div>
                    <CardTitle className="text-royal-blue flex items-center gap-2">
                      {social.name}
                      <ExternalLink className="w-4 h-4" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>{social.description}</CardDescription>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Book Shaba Section */}
      <section className="bg-royal-purple py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Calendar className="w-12 h-12 text-kingdom-gold mx-auto mb-4" />
            <h2 className="font-heading text-3xl text-white mb-4">Book Shaba Sharah</h2>
            <p className="text-purple-200">
              Interested in booking Shaba Sharah for your event, feast day celebration, or gathering? 
              Fill out the form below and we'll get back to you.
            </p>
          </div>

          {submitted ? (
            <Card className="bg-white">
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="font-heading text-2xl text-royal-blue mb-2">Request Submitted!</h3>
                <p className="text-slate-600">
                  Thank you for your booking request. We will review your details and get back to you soon.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white">
              <CardContent className="p-8">
                <form onSubmit={handleBookingSubmit} className="space-y-6" data-testid="booking-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="form-label">Name *</Label>
                      <Input
                        value={bookingForm.name}
                        onChange={(e) => handleBookingChange("name", e.target.value)}
                        placeholder="Your name or organization"
                        className="form-input"
                        required
                        style={{ fontSize: "16px" }}
                      />
                    </div>
                    <div>
                      <Label className="form-label">Email *</Label>
                      <Input
                        type="email"
                        value={bookingForm.email}
                        onChange={(e) => handleBookingChange("email", e.target.value)}
                        placeholder="your@email.com"
                        className="form-input"
                        required
                        style={{ fontSize: "16px" }}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="form-label">Country *</Label>
                      <Input
                        value={bookingForm.country}
                        onChange={(e) => handleBookingChange("country", e.target.value)}
                        placeholder="Country of event"
                        className="form-input"
                        required
                        style={{ fontSize: "16px" }}
                      />
                    </div>
                    <div>
                      <Label className="form-label">Event Date *</Label>
                      <Input
                        type="date"
                        value={bookingForm.date}
                        onChange={(e) => handleBookingChange("date", e.target.value)}
                        className="form-input"
                        required
                        style={{ fontSize: "16px" }}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="form-label">Budget Range</Label>
                    <Input
                      value={bookingForm.budget}
                      onChange={(e) => handleBookingChange("budget", e.target.value)}
                      placeholder="e.g., $500-$1000"
                      className="form-input"
                      style={{ fontSize: "16px" }}
                    />
                  </div>

                  <div>
                    <Label className="form-label">Event Details</Label>
                    <Textarea
                      value={bookingForm.details}
                      onChange={(e) => handleBookingChange("details", e.target.value)}
                      placeholder="Tell us about your event, venue, expected attendance, and any special requirements..."
                      className="form-input min-h-[120px]"
                      style={{ fontSize: "16px" }}
                    />
                  </div>

                  <Button type="submit" className="w-full btn-gold" disabled={submitting}>
                    {submitting ? (
                      "Submitting..."
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Booking Request
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Recent Teachings */}
      <section className="bg-parchment py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-section text-royal-blue mb-8">Recent Teachings</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {RECENT_TEACHINGS.map((teaching) => (
              <Card key={teaching.title} className="card-official">
                <CardHeader>
                  <p className="text-kingdom-gold text-sm">{teaching.date}</p>
                  <CardTitle className="text-royal-blue">{teaching.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{teaching.description}</CardDescription>
                  <a
                    href="https://www.youtube.com/@TheWomanInGold77"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-kingdom-gold text-sm mt-4 hover:underline"
                  >
                    Watch on YouTube
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-kingdom-gold py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-black mb-4">
            Subscribe to Stay Connected
          </h2>
          <p className="text-black/80 mb-6">
            Follow our channels for the latest teachings, music, and nation updates.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://www.youtube.com/@TheWomanInGold77" target="_blank" rel="noopener noreferrer">
              <Button className="bg-scarlett hover:bg-scarlett/90 text-white">
                <Play className="w-4 h-4 mr-2" />
                YouTube
              </Button>
            </a>
            <a href="https://www.tiktok.com/@theqadashsheep77" target="_blank" rel="noopener noreferrer">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                TikTok
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
